import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { Users, Activity, ShieldAlert, Brain, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { supabase } from '../../services/supabase';

export const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeSignals: 0,
    });
    const [macroBias, setMacroBias] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Fetch stats
                const { count: userCount } = await supabase.from('user_profiles').select('*', { count: 'exact', head: true });
                const { count: signalCount } = await supabase.from('signals').select('*', { count: 'exact', head: true }).eq('result', 'active');

                setStats({
                    totalUsers: userCount || 0,
                    activeSignals: signalCount || 0,
                });

                // Fetch the latest active macro bias
                const { data: biasData, error: biasError } = await supabase
                    .from('macro_bias')
                    .select('*')
                    .eq('is_active', true)
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .single();

                if (!biasError && biasData) {
                    setMacroBias(biasData);
                }
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const getBiasColor = (bias: string) => {
        if (bias === 'BULLISH') return 'text-green-500 bg-green-500/10 border-green-500/30';
        if (bias === 'BEARISH') return 'text-red-500 bg-red-500/10 border-red-500/30';
        return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30';
    };

    const getBiasIcon = (bias: string) => {
        if (bias === 'BULLISH') return <TrendingUp className="w-6 h-6 text-green-500" />;
        if (bias === 'BEARISH') return <TrendingDown className="w-6 h-6 text-red-500" />;
        return <Minus className="w-6 h-6 text-yellow-500" />;
    };

    return (
        <AdminLayout>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">Overview</h1>
                <p className="text-gray-400">System-wide statistics and performance.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Stat Cards */}
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-gray-400 font-medium">Total Users</h3>
                        <Users className="text-finance-accent w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-white">{loading ? '--' : stats.totalUsers}</p>
                </div>

                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-gray-400 font-medium">Active Signals</h3>
                        <Activity className="text-blue-500 w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-white">{loading ? '--' : stats.activeSignals}</p>
                </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-4 mt-8 flex items-center gap-2">
                <Brain className="w-6 h-6 text-finance-accent" />
                Live AI Gatekeeper Bias
            </h2>

            {loading ? (
                <div className="animate-pulse flex space-x-4 p-6 bg-finance-card rounded-xl border border-gray-800">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                        <div className="space-y-2">
                            <div className="h-4 bg-gray-700 rounded"></div>
                            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                        </div>
                    </div>
                </div>
            ) : macroBias ? (
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6 mb-8 flex flex-col gap-6">
                    <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getBiasColor(macroBias.bias)}`}>
                            {getBiasIcon(macroBias.bias)}
                            <span className="font-bold text-lg tracking-wider">{macroBias.bias}</span>
                        </div>
                        <div className="text-gray-400">
                            Generated on: {new Date(macroBias.created_at).toLocaleString()}
                        </div>
                    </div>

                    <div className="w-full">
                        <h3 className="text-finance-accent font-semibold mb-3">Institutional Breakdown:</h3>
                        <div className="bg-gray-900/50 p-6 rounded-lg border border-gray-800 text-gray-300 font-mono text-sm leading-relaxed whitespace-pre-wrap">
                            {macroBias.rationale}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6 mb-8">
                    <p className="text-gray-400 text-center py-4">No active macro bias generated yet. Edge Function has not been triggered natively.</p>
                </div>
            )}

        </AdminLayout>
    );
};
